import Peer from 'peerjs';
import {User} from '../../../../shared/entities/User';
import {VideoPlayer} from './VideoPlayer';
import Entity, {
	IntegerEntityAttribute,
	StringEntityAttribute,
	BooleanEntityAttribute,
	EntityEntityAttribute,
} from '../../../../shared/base/Entity';

/**
 * @property {VideoPlayer} video
 * @property {VideoSyncPeer[]} peers
 */
export class VideoSyncService {
	static State = {
		Preparing: 0,
		Ready: 1,
		Connecting: 2,
		Connected: 3,
		Hosting: 4
	};

	/**
	 * @param {VideoPlayer} video
	 * @param {User} user
	 */
	constructor(video, user, router) {
		this.state = VideoSyncService.State.Preparing;
		this.user = user;
		this.router = router;

		this.setVideo(video);

		this.start();
	}

	start() {
		if (this.peer && this.peer.id) this.peer.destroy();

		this.peers = [];

		this.peer = new Peer(null, {
			debug: 2
		});

		this.peer.once('open', () => {
			this.selfPeer.peerId = this.peer.id;
			this.state = VideoSyncService.State.Ready;
		});

		this.peer.on('connection', (conn) => {
			this.newPeer(conn);
		});

		this.selfPeer = new VideoSyncPeer({
			user: this.user
		})
	}

	destroy() {
		this.peer.destroy();
	}

	/**
	 * @param {VideoPlayer} video
	 */
	setVideo(video) {
		this.video = video;
		this.videoEventsEnabled = true;

		video.on(VideoPlayer.Events.play, () => {
			if (!this.videoEventsEnabled) return;

			this.send(VideoSyncPeer.Actions.play);
			this.selfPeer.playing = video.playing;

			this.sendState();
		});

		video.on(VideoPlayer.Events.pause, () => {
			if (!this.videoEventsEnabled) return;

			this.send(VideoSyncPeer.Actions.pause);
			this.selfPeer.playing = video.playing;

			this.sendState();
		});

		video.on(VideoPlayer.Events.seek, (position) => {
			if (!this.videoEventsEnabled) return;

			this.send(VideoSyncPeer.Actions.seek, position);

			if (this.video.playing) {
				this.video.pause();

				setTimeout(() => {
					this.video.play();
				}, 1000);
			}
		});

		video.on(VideoPlayer.Events.updatePosition, () => {
			if (!this.videoEventsEnabled) return;

			this.selfPeer.position = video.position;
			this.sendState();
		});

		video.on(VideoPlayer.Events.updateCanPlay, () => {
			if (!this.videoEventsEnabled) return;

			this.selfPeer.canPlay = video.canPlay;
			this.sendState();
		});
	}

	disableVideoEventsForThisLoop() {
		this.videoEventsEnabled = false;

		setTimeout(() => {
			this.videoEventsEnabled = true;
		}, 0)
	}

	connect(peerId) {
		if (!this.peer.open) {
			this.peer.once('open', () => {
				this.connect(peerId);
			});
			return;
		}

		if (this.peers.find(p => p.peerId === peerId)) {
			return;
		}

		this.state = VideoSyncService.State.Connecting;
		const peer = this.newPeer(this.peer.connect(peerId, {
			reliable: true
		}));

		peer.on(VideoSyncPeer.Events.identified, () => {
			this.state = VideoSyncService.State.Connected;

			if (this.peers.length === 1) {
				this.syncToPeer(peer);
			}
		});
	}

	/**
	 * @param {Peer.DataConnection} conn
	 * @returns {VideoSyncPeer}
	 */
	newPeer(conn) {
		const peer = new VideoSyncPeer({
			peerId: conn.peer
		}, conn);

		conn.on('open', () => {
			this.sendState(peer);
		});

		peer.on(VideoSyncPeer.Events.identified, () => {
			this.addPeer(peer);
		});

		peer.on(VideoSyncPeer.Events.die, () => {
			this.removePeer(peer);
		});

		return peer;
	}

	/**
	 * @param {VideoSyncPeer} peer
	 */
	addPeer(peer) {
		this.send(VideoSyncPeer.Actions.newPeer, peer.peerId);

		this.peers.push(peer);

		peer.on(VideoSyncPeer.Events.play, () => {
			this.disableVideoEventsForThisLoop();

			if (!this.video.playing) {
				this.video.play();
			}
		});

		peer.on(VideoSyncPeer.Events.pause, () => {
			this.disableVideoEventsForThisLoop();

			if (this.video.playing) {
				this.video.pause();
			}
		});

		peer.on(VideoSyncPeer.Events.seek, (position) => {
			this.disableVideoEventsForThisLoop();

			if (this.video.position !== position) {
				this.video.pause();
				this.video.seek(position);
			}
		});

		peer.on(VideoSyncPeer.Events.updateState, () => {
			if (this.state === VideoSyncService.State.Connected) {
				let delta = Math.abs(this.video.position - peer.position);

				if (delta > 2) {
					this.syncToPeer(peer);
				}
			}
		});

		peer.on(VideoSyncPeer.Events.newPeer, (peerId) => {
			this.connect(peerId);
		});

		peer.on(VideoSyncPeer.Events.routeChange, (route) => {
			if (this.router.currentRoute.fullPath !== route) {
				this.router.push(route);
			}
		});
	}

	/**
	 * @param {VideoSyncPeer} peer
	 */
	syncToPeer(peer) {
		this.disableVideoEventsForThisLoop();

		this.video.seek(peer.position);
	}

	removePeer(peer) {
		this.peers.remove(peer);

		peer.off(VideoSyncPeer.Events.play);

		peer.off(VideoSyncPeer.Events.pause);

		if (!this.peers.length) {
			this.state = VideoSyncService.State.Ready;
		}
	}

	sendState(peer = null) {
		(peer || this).send(VideoSyncPeer.Actions.updateState, this.selfPeer.Serialize());
	}

	send(key, data = null) {
		this.peers.forEach(p => p.send(key, data));
	}

	routeChange() {
		const newRoute = this.router.currentRoute.fullPath;

		this.send(VideoSyncPeer.Events.routeChange, newRoute);
		this.selfPeer.position = 0;

		this.video.once(VideoPlayer.Events.play, () => {
			this.video.pause();

			setTimeout(() => {
				this.video.play();
			}, 2000);
		});
	}
}


/**
 * @property {String} peerId
 * @property {User} user
 * @property {Number} position
 * @property {Boolean} playing
 * @property {Boolean} canPlay
 */
export class VideoSyncPeer extends Entity {
	static Events = {
		identified: 'identified',
		die: 'die',
		play: 'play',
		pause: 'pause',
		seek: 'seek',
		updateState: 'updateState',
		newPeer: 'newPeer',
		routeChange: 'routeChange',
	};

	static Actions = {
		updateState: 'updateState',
		play: 'play',
		pause: 'pause',
		seek: 'seek',
		newPeer: 'newPeer',
		routeChange: 'routeChange',
	};

	/**
	 * @param {Object} state
	 * @param {Peer.DataConnection} conn
	 */
	constructor(state, conn = null) {
		super(state);

		if (conn) {
			this.conn = conn;

			conn.on('data', (msg) => {
				this.handleMessage(msg);
			});

			conn.on("close", () => {
				this.emit(VideoSyncPeer.Events.die);
			});

			conn.on("error", () => {
				this.emit(VideoSyncPeer.Events.die);
			});
		}
	}

	updateState(user) {
		let identified = !this.user;

		this.FillFromArray(user);

		if (identified) this.emit(VideoSyncPeer.Events.identified);

		this.emit(VideoSyncPeer.Events.updateState);
	}

	play() {
		this.emit(VideoSyncPeer.Events.play);
	}

	pause() {
		this.emit(VideoSyncPeer.Events.pause);
	}

	seek(position) {
		this.emit(VideoSyncPeer.Events.seek, position);
	}

	newPeer(peerId) {
		this.emit(VideoSyncPeer.Events.newPeer, peerId);
	}

	routeChange(route) {
		this.emit(VideoSyncPeer.Events.routeChange, route);
	}

	handleMessage(msg) {
		if (msg && msg.key) {
			if (this.constructor.Actions.hasOwnProperty(msg.key)){
				console.log(msg);

				this[msg.key](msg.data);
			} else {
				console.error('Wrong message key');
			}
		} else {
			console.error('Wrong message format');
		}
	}

	send(key, data) {
		if (!this.conn.open) return;

		this.conn.send({
			key,
			data
		})
	}
}

VideoSyncPeer.Register();
VideoSyncPeer.Attributes = [
	new StringEntityAttribute('peerId'),
	new EntityEntityAttribute('user', User),
	new IntegerEntityAttribute('position'),
	new BooleanEntityAttribute('playing'),
	new BooleanEntityAttribute('canPlay')
];